import React, { useContext, useEffect, useState } from 'react';
import { Grid, InputLabel, Paper, Typography } from '@material-ui/core';
import { Link } from 'react-router-dom';

import { UserContext } from 'contexts/UserContext';
import { DataTable } from 'shared';
import { FBMPartnerOrderStatusChip } from 'shared/components/fbm/FBMPartnerOrderStatusChip';

const summaryOrderValue = { fontWeight: 600, fontSize: '1.05rem', lineHeight: 1.4 };

function SummaryField({ children, label }) {
    return (
        <div style={{ minHeight: 72 }}>
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
                {label}
            </Typography>
            {children}
        </div>
    );
}

function DeepDiveResults({ deepDiveResults }) {
    const [thirdPartyInfoColumns, setThirdPartyInfoColumns] = useState([]);
    const { fbmAccount, user } = useContext(UserContext);
    const [shipToCompare, setShipToCompare] = useState({});

    const summaryColumns = [
        { accessor: 'shipmentId', Header: 'Shipment Id' },
        { accessor: 'shipStatus', Header: 'ShipStatus' },
        { accessor: 'trackingUrl', Header: 'Tracking Url' },
        { accessor: 'hub', Header: 'Hub' },
        { accessor: 'carrier', Header: 'Carrier' },
    ];

    const ingestColumn = [
        { accessor: 'insertedAt', Header: 'Recorded At' },
        { accessor: 'type', Header: 'Type' },
        { accessor: 'details', Header: 'Details' },
    ];

    const eventColumn = [
        { accessor: 'shipmentId', Header: 'ShipmentId' },
        { accessor: 'timestamp', Header: 'Timestamp' },
        { accessor: 'type', Header: 'Event' },
        { accessor: 'details', Header: 'Details' },
    ];

    const itemMMCol = [
        { accessor: 'shipmentId', Header: 'Shipment Id' },
        { accessor: 'name', Header: 'Item Name' },
        { accessor: 'externalSku', Header: 'External SKU' },
        { accessor: 'thirdPartyItemID', Header: `${fbmAccount.companyName} SKU` },
        { accessor: 'qty', Header: 'Amount' },
    ];

    const thirdPartyItemsColumns = [
        { accessor: 'name', Header: 'Item Name' },
        { accessor: 'thirdPartyItemID', Header: `${fbmAccount.companyName} SKU` },
        { accessor: 'qty', Header: 'Amount' },
    ];

    useEffect(() => {
        if (fbmAccount.fbmAccountName === 'littlespoon') {
            setThirdPartyInfoColumns([
                { accessor: 'createdOn', Header: 'Ordered At' },
                { accessor: 'ssOrderID', Header: 'ShipStation Order ID' },
                { accessor: 'status', Header: 'Status' },
                { accessor: 'warehouse', Header: 'Warehouse' },
                { accessor: 'isFulfilledByMisfits', Header: 'In Misfits System?' },
            ]);
        }
    }, [fbmAccount]);

    useEffect(() => {
        const { shipToInternal } = deepDiveResults;
        const { shipToExternal } = deepDiveResults;
        setShipToCompare({
            lineOne: shipToInternal?.lineOne?.toLowerCase() === shipToExternal.lineOne?.toLowerCase(),
            lineTwo: shipToInternal?.lineTwo?.toLowerCase() === shipToExternal.lineTwo?.toLowerCase(),
            city: shipToInternal?.city?.toLowerCase() === shipToExternal.city?.toLowerCase(),
            province: shipToInternal?.province?.toLowerCase() === shipToExternal.province?.toLowerCase(),
            zip: shipToInternal?.zip === shipToExternal.zip,
        });
    }, [deepDiveResults]);

    const addressTextColor = (flag) => (flag ? 'black' : 'red');

    const { mmOrderNum } = deepDiveResults;
    const showMmOrderLink = !user.fbm && mmOrderNum != null && mmOrderNum !== '' && Number(mmOrderNum) !== 0;

    const mmOrderNumberNode = showMmOrderLink ? (
        <Typography variant="subtitle1" component="p" style={{ ...summaryOrderValue, margin: 0 }}>
            <Link
                to={`/system/order-deep-dive?orderNumber=${encodeURIComponent(mmOrderNum)}`}
                style={{ color: 'inherit', textDecoration: 'underline' }}
            >
                {mmOrderNum}
            </Link>
        </Typography>
    ) : (
        <Typography variant="subtitle1" component="p" style={{ ...summaryOrderValue, margin: 0 }}>
            {mmOrderNum}
        </Typography>
    );

    return (
        <>
            <Paper variant="outlined" style={{ padding: 24, marginBottom: 24 }}>
                <Typography variant="h6" component="h2" gutterBottom style={{ fontWeight: 600 }}>
                    Summary Information
                </Typography>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'nowrap',
                        gap: 24,
                        alignItems: 'stretch',
                        overflowX: 'auto',
                        paddingBottom: 4,
                    }}
                >
                    {[
                        {
                            key: 'partnerOrder',
                            label: `${fbmAccount.companyName} Order Number`,
                            node: (
                                <Typography
                                    variant="subtitle1"
                                    component="p"
                                    style={{ ...summaryOrderValue, margin: 0 }}
                                >
                                    {deepDiveResults.orderNum}
                                </Typography>
                            ),
                        },
                        {
                            key: 'mmOrder',
                            label: 'Misfits Order Number',
                            node: mmOrderNumberNode,
                        },
                        {
                            key: 'ingest',
                            label: 'Ingest status',
                            node: (
                                <FBMPartnerOrderStatusChip
                                    kind="ingest"
                                    value={deepDiveResults.fbmPartnerOrderIngestStatus}
                                />
                            ),
                        },
                        {
                            key: 'retry',
                            label: 'Retry count',
                            node: (
                                <Typography
                                    variant="subtitle1"
                                    component="p"
                                    style={{ ...summaryOrderValue, margin: 0 }}
                                >
                                    {deepDiveResults.fbmPartnerOrderRetryCount ?? '—'}
                                </Typography>
                            ),
                        },
                        {
                            key: 'ack',
                            label: 'Acknowledgment status',
                            node: (
                                <FBMPartnerOrderStatusChip
                                    kind="acknowledgment"
                                    value={deepDiveResults.fbmPartnerOrderAcknowledgmentStatus}
                                />
                            ),
                        },
                    ].map(({ key, label, node }) => (
                        <div key={key} style={{ flex: '1 1 0', minWidth: 112 }}>
                            <SummaryField label={label}>{node}</SummaryField>
                        </div>
                    ))}
                </div>
            </Paper>
            <DataTable columns={summaryColumns} data={deepDiveResults.shipmentInfo} />
            <br />
            <h2>Misfits Address Information</h2>
            <Grid container>
                <Grid item xs>
                    <InputLabel>Line One</InputLabel>
                    <span style={{ color: addressTextColor(shipToCompare.lineOne) }}>
                        {deepDiveResults.shipToInternal?.lineOne}
                    </span>
                </Grid>
                <Grid item xs>
                    <InputLabel>Line Two</InputLabel>
                    <span style={{ color: addressTextColor(shipToCompare.lineTwo) }}>
                        {deepDiveResults.shipToInternal?.lineTwo}
                    </span>
                </Grid>
                <Grid item xs>
                    <InputLabel>City</InputLabel>
                    <span style={{ color: addressTextColor(shipToCompare.city) }}>
                        {deepDiveResults.shipToInternal?.city}
                    </span>
                </Grid>
                <Grid item xs>
                    <InputLabel>State</InputLabel>
                    <span style={{ color: addressTextColor(shipToCompare.province) }}>
                        {deepDiveResults.shipToInternal?.province}
                    </span>
                </Grid>
                <Grid item xs>
                    <InputLabel>Zip</InputLabel>
                    <span style={{ color: addressTextColor(shipToCompare.zip) }}>
                        {deepDiveResults.shipToInternal?.zip}
                    </span>
                </Grid>
            </Grid>
            <br />
            <h2>{fbmAccount.companyName} Address Information</h2>
            <Grid container>
                <Grid item xs>
                    <InputLabel>Line One</InputLabel>
                    <span style={{ color: addressTextColor(shipToCompare.lineOne) }}>
                        {deepDiveResults.shipToExternal.lineOne}
                    </span>
                </Grid>
                <Grid item xs>
                    <InputLabel>Line Two</InputLabel>
                    <span style={{ color: addressTextColor(shipToCompare.lineTwo) }}>
                        {deepDiveResults.shipToExternal.lineTwo}
                    </span>
                </Grid>
                <Grid item xs>
                    <InputLabel>City</InputLabel>
                    <span style={{ color: addressTextColor(shipToCompare.city) }}>
                        {deepDiveResults.shipToExternal.city}
                    </span>
                </Grid>
                <Grid item xs>
                    <InputLabel>State</InputLabel>
                    <span style={{ color: addressTextColor(shipToCompare.province) }}>
                        {deepDiveResults.shipToExternal.province}
                    </span>
                </Grid>
                <Grid item xs>
                    <InputLabel>Zip</InputLabel>
                    <span style={{ color: addressTextColor(shipToCompare.zip) }}>
                        {deepDiveResults.shipToExternal.zip}
                    </span>
                </Grid>
            </Grid>
            <br />
            <h2>{deepDiveResults.thirdPartyName} Records</h2>
            <DataTable columns={thirdPartyInfoColumns} data={deepDiveResults.thirdPartyInfo} />
            <br />
            <h2>Ingest Log</h2>
            <DataTable columns={ingestColumn} data={deepDiveResults.digestEvents} />
            <br />
            <Grid container>
                <Grid item xs={6}>
                    <h2>Items ({deepDiveResults.thirdPartyName})</h2>
                    <DataTable columns={thirdPartyItemsColumns} data={deepDiveResults.thirdPartyItems} />
                </Grid>
                <Grid item xs={6}>
                    <h2>Items (Misfits)</h2>
                    <DataTable columns={itemMMCol} data={deepDiveResults.mmItems} />
                </Grid>
            </Grid>
            <h2>Event Log</h2>
            <DataTable columns={eventColumn} data={deepDiveResults.shipEvents} />
        </>
    );
}

export { DeepDiveResults };
